import React, { useRef, useState, useEffect, useCallback } from 'react';
import DocumentList from './DocumentList';
import { UserConfigService } from '../../utils/apiService';
import { Link } from 'react-router-dom';
import { useConfig } from '../../contexts/ConfigContext';

const DocumentManagement: React.FC = () => {
  const documentListRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasValidConfig, setHasValidConfig] = useState(true);
  // Add state to track retries and prevent infinite loops
  const [configCheckAttempt, setConfigCheckAttempt] = useState(0);
  const maxConfigCheckAttempts = 3;
  const configCheckRef = useRef<number | null>(null);
  const lastConfigCheckRef = useRef<number>(0);
  
  // Get configuration context
  const { configState, hasConfigurations, configurations } = useConfig();

  const checkConfigurationValidity = useCallback(async (force = false) => {
    // Prevent excessive config checks within 30 seconds
    const currentTime = Date.now();
    const timeSinceLastCheck = currentTime - lastConfigCheckRef.current;
    
    if (!force && timeSinceLastCheck < 30000) {
      // Disable verbose logging to reduce console noise
      if (false) { // Enable only when debugging configuration issues
        console.log('Skipping configuration check - checked recently');
      }
      return;
    }
    
    // Prevent checking while auth flow is in progress
    const authFlowInProgress = sessionStorage.getItem('auth_flow_in_progress') === 'true';
    if (authFlowInProgress) {
      return;
    }
    
    try {
      // Disable verbose logging to reduce console noise
      if (false) { // Enable only when debugging configuration issues
        console.log(`Checking configuration validity (attempt ${configCheckAttempt + 1}/${maxConfigCheckAttempts})...`);
      }
      
      const configurations = await UserConfigService.getConfigurations();
      
      // Disable verbose logging to reduce console noise
      if (false) { // Enable only when debugging configuration issues
        console.log("Configurations received:", configurations);
      }
      
      if (!configurations || configurations.length === 0) {
        // Disable verbose logging to reduce console noise
        if (false) { // Enable only when debugging configuration issues
          console.log("No configurations found or invalid configuration data");
        }
        setHasValidConfig(false);
        lastConfigCheckRef.current = currentTime;
        return;
      }

      // Check if there's at least one configuration with a valid folder_id
      const validConfigs = configurations.filter(config => 
        config && 
        config.folder_id && 
        config.folder_id.trim() !== ""
        // Remove the is_active check for now to debug
        // && config.is_active === true
      );
      
      // Add debug logging temporarily
      if (import.meta.env.DEV) {
        console.log("[DocumentManagement] Configuration validation:", {
          totalConfigs: configurations.length,
          validConfigs: validConfigs.length,
          configs: configurations,
          validConfigsData: validConfigs
        });
      }
      setHasValidConfig(validConfigs.length > 0);
      lastConfigCheckRef.current = currentTime;
    } catch (error) {
      // Keep error logging for important issues
      if (import.meta.env.DEV) {
        console.error("Error checking configuration validity:", error);
      }
      if (configCheckAttempt < maxConfigCheckAttempts - 1) {
        // Disable verbose logging to reduce console noise
        if (false) { // Enable only when debugging configuration issues
          console.log(`Will retry configuration check (${configCheckAttempt + 1}/${maxConfigCheckAttempts})...`);
        }
        setConfigCheckAttempt(prev => prev + 1);
        setTimeout(() => {
          checkConfigurationValidity(true);
        }, 2000);
      } else {
        // Disable verbose logging to reduce console noise
        if (false) { // Enable only when debugging configuration issues
          console.log("Max configuration check attempts reached, assuming no valid configuration");
        }
        setHasValidConfig(false);
        lastConfigCheckRef.current = currentTime;
      }
    }
  }, [configCheckAttempt, maxConfigCheckAttempts]);

  // Check if a valid configuration exists
  useEffect(() => {
    const checkConfigValidity = async () => {
      try {
        setIsLoading(true);
        // Disable verbose logging to reduce console noise
        if (false) { // Enable only when debugging configuration issues
          console.log(`Checking configuration validity (attempt ${configCheckAttempt + 1}/${maxConfigCheckAttempts})...`);
        }
        
        // Get all configurations
        const configurations = await UserConfigService.getConfigurations();
        
        // Disable verbose logging to reduce console noise
        if (false) { // Enable only when debugging configuration issues
          console.log("Configurations received:", configurations);
        }
        
        // More aggressive check: Consider it invalid if configurations array is empty or undefined
        if (!configurations || !Array.isArray(configurations) || configurations.length === 0) {
          // Disable verbose logging to reduce console noise
          if (false) { // Enable only when debugging configuration issues
            console.log("No configurations found or invalid configuration data");
          }
          // Set state to show the configuration setup UI instead of repeatedly checking
          setHasValidConfig(false);
          setIsLoading(false);
          
          // Store in session that we've determined there are no configurations
          // This prevents repeated checks causing refresh loops
          sessionStorage.setItem('kms_no_configurations_detected', 'true');
          return;
        }
        
        // Check if there's at least one configuration with a valid folder_id
        const validConfigs = configurations.filter(config => 
          config && 
          config.folder_id && 
          config.folder_id.trim() !== ""
          // Remove the is_active check for now to debug
          // && config.is_active === true
        );
        
        // Disable verbose logging to reduce console noise
        if (false) { // Enable only when debugging configuration issues
          console.log("Valid configurations found:", validConfigs.length);
        }
        setHasValidConfig(validConfigs.length > 0);
        
        // Clear the flag since we've successfully checked
        sessionStorage.removeItem('kms_no_configurations_detected');
      } catch (error) {
        // Keep error logging for important issues, but only in dev
        if (import.meta.env.DEV) {
          console.error("Error checking configuration validity:", error);
        }
        
        // Don't retry more than maxConfigCheckAttempts times to prevent infinite loops
        if (configCheckAttempt < maxConfigCheckAttempts - 1) {
          // Disable verbose logging to reduce console noise
          if (false) { // Enable only when debugging configuration issues
            console.log(`Will retry configuration check (${configCheckAttempt + 1}/${maxConfigCheckAttempts})...`);
          }
          setConfigCheckAttempt(prev => prev + 1);
          
          // Schedule the next check attempt with increasing delay
          if (configCheckRef.current) {
            clearTimeout(configCheckRef.current);
          }
          configCheckRef.current = setTimeout(() => {
            checkConfigValidity();
          }, 1000 * (configCheckAttempt + 1)); // Increasing backoff
        } else {
          // We've reached max attempts, assume no valid configuration
          // Disable verbose logging to reduce console noise
          if (false) { // Enable only when debugging configuration issues
            console.log("Max configuration check attempts reached, assuming no valid configuration");
          }
          setHasValidConfig(false);
          sessionStorage.setItem('kms_no_configurations_detected', 'true');
        }
        
        // Be more aggressive - treat any error as invalid configuration
        setHasValidConfig(false);
      } finally {
        setIsLoading(false);
      }
    };
    
    // Check if we've already determined there are no configurations
    const noConfigsDetected = sessionStorage.getItem('kms_no_configurations_detected') === 'true';
    if (noConfigsDetected) {
      // Disable verbose logging to reduce console noise
      if (false) { // Enable only when debugging configuration issues
        console.log("Previously determined no configurations exist, skipping check");
      }
      setHasValidConfig(false);
      setIsLoading(false);
      return;
    }
    
    // Only check if we haven't exceeded max attempts
    if (configCheckAttempt < maxConfigCheckAttempts) {
      checkConfigValidity();
    }
    
    // Cleanup timeout on unmount
    return () => {
      if (configCheckRef.current) {
        clearTimeout(configCheckRef.current);
      }
    };
  }, [configCheckAttempt]);

  // Add a single check for document access (instead of continuous polling)
  useEffect(() => {
    // Function to check if we should show the configuration UI
    const checkDocumentAccess = async () => {
      // If we've already determined there are no configurations, skip the check
      const noConfigsDetected = sessionStorage.getItem('kms_no_configurations_detected') === 'true';
      if (noConfigsDetected) {
        // Disable verbose logging to reduce console noise
        if (false) { // Enable only when debugging configuration issues
          console.log("DocumentManagement: Previously determined no configurations exist, skipping document access check");
        }
        return;
      }

      // Only check once on component mount
      // Disable verbose logging to reduce console noise
      if (false) { // Enable only when debugging configuration issues
        console.log("DocumentManagement: Checking document access...");
      }
      
      try {
        // Try to load configurations to verify access
        const configurations = await UserConfigService.getConfigurations();
        if (!configurations || configurations.length === 0) {
          // Disable verbose logging to reduce console noise
          if (false) { // Enable only when debugging configuration issues
            console.log("DocumentManagement: No configurations found, user needs to configure");
          }
          setHasValidConfig(false);
          sessionStorage.setItem('kms_no_configurations_detected', 'true');
        }
      } catch (error) {
        // Disable verbose logging to reduce console noise
        if (false) { // Enable only when debugging configuration issues
          console.log("DocumentManagement: Error accessing configurations, may need configuration");
        }
        // Don't assume no config on error - let the main config check handle it
      }
    };
    
    // Run only once with a brief delay to allow other components to load
    const timeoutId = setTimeout(checkDocumentAccess, 2000);
    
    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  // Listen for configuration changes from ConfigContext
  useEffect(() => {
    if (hasConfigurations && configurations && configurations.length > 0) {
      console.log('[DocumentManagement] ConfigContext reports configurations exist, updating state');
      setHasValidConfig(true);
      setIsLoading(false);
      sessionStorage.removeItem('kms_no_configurations_detected');
    } else if (configState === 'unconfigured') {
      console.log('[DocumentManagement] ConfigContext reports no configurations');
      setHasValidConfig(false);
      setIsLoading(false);
    }
  }, [configState, hasConfigurations, configurations]);

  if (isLoading) {
    return (
      <div className="flex-1 bg-gray-50 dark:bg-gray-900 w-full h-full overflow-auto">
        <div className="w-full h-full max-w-full py-6 px-6 flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-blue-600 dark:border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!hasValidConfig) {
    return (
      <div className="flex-1 bg-gray-50 dark:bg-gray-900 w-full h-full overflow-auto">
        <div className="w-full h-full max-w-full py-6 px-6">
          <div className="text-center py-16 border rounded-lg bg-white dark:bg-gray-800 dark:border-gray-700 max-w-2xl mx-auto">
            <div className="mx-auto h-24 w-24 flex items-center justify-center rounded-full bg-blue-50 dark:bg-blue-900/20">
              <svg 
                className="h-16 w-16 text-blue-500"
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor" 
                aria-hidden="true"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={1.5} 
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              </svg>
            </div>
            
            <h3 className="mt-4 text-lg font-medium text-blue-700 dark:text-blue-400">
              Configurazione Necessaria
            </h3>
            
            <p className="mt-2 text-md text-gray-600 dark:text-gray-400 max-w-md mx-auto">
              Nessun documento caricato da Google Drive. Vai in Configurazioni e aggiungi il tuo folder ID.
            </p>
            
            <div className="mt-6">
              <Link
                to="/configurazioni"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Vai alla pagina Configurazioni
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-gray-50 dark:bg-gray-900 w-full h-full overflow-auto">
      <div className="w-full h-full max-w-full py-6 px-6">
        <div className="flex flex-col space-y-4">
          <div ref={documentListRef}>
            <DocumentList />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentManagement; 